import ModalFullScreenWTN from 'components/generic/ModalFullScreenWTN'
import styles from './addPlayerModal.module.scss'
import { PostScoreModalId } from 'constants/modals'
import React, { useCallback, useRef } from 'react'
import SearchBar from 'components/search/SearchBar'
import {
  FAQS_MY_MATCHES_HREF,
  MIN_SEARCH_CHARS,
  TABLET_BREAKPOINT,
  WALK_UP_REG_FULL_URL,
} from 'constants/global'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { suggestPlayers, setSearchQuery } from 'actions/search-actions'
import { AppState } from 'redux/reducers'
import PlayerSuggestion from 'components/search/PlayerSuggestion'
import { Player } from 'types/global'
import PlayerFilterBar from 'components/search/PlayerFilterBar'
import AddPlayerSearchResults from '../AddPlayerSearchResults'
import BtnPrimary from 'components/generic/BtnPrimary'
import { copyToClipboard } from 'utils'
import useWatchlists from 'hooks/use-watchlists'
import useWindowSize from 'hooks/use-windowSize'

const DESKTOP_LAYOUT_BREAKPOINT = 890
interface Props {
  selectedPlayersIdsMap: Record<string, boolean>
  onSelectPlayer: (player: Player) => void
}

const AddPlayerModal = React.memo(({ selectedPlayersIdsMap, onSelectPlayer }: Props) => {
  const { t: tSocialMatches } = useTranslation('strSocialMatches')
  const { t: tSearchBar } = useTranslation('strSearchBar')
  const dispatch = useDispatch()
  const suggestions = useSelector(
    (state: AppState) => state.searchbar.suggestions[PostScoreModalId.ADD_PLAYER]
  )
  const isLoadingSuggestions = useSelector(
    (state: AppState) => state.searchbar.suggestionsPending[PostScoreModalId.ADD_PLAYER]
  )
  const searchQuery = useSelector(
    (state: AppState) => state.searchbar.searchQuery[PostScoreModalId.ADD_PLAYER]
  )
  const filtersStyles = useRef({
    wrapper: styles.filtersWrapper,
    sectionContainer: styles.filtersSection,
    desktopFiltersContainer: styles.desktopFiltersContainer,
  })
  const { isPlayerWatched } = useWatchlists()
  const getSuggestions = useCallback(
    search => {
      dispatch(
        suggestPlayers(search, PostScoreModalId.ADD_PLAYER, { scopeForSelfSubmission: true })
      )
    },
    [dispatch]
  )
  const handleRenderSuggestion = useCallback(
    (suggestion: Player) => {
      const isAlreadySelected = selectedPlayersIdsMap[suggestion.id]

      return (
        <PlayerSuggestion
          player={suggestion}
          onClick={
            isAlreadySelected
              ? undefined
              : () => {
                  onSelectPlayer(suggestion)
                }
          }
          showWatchlistStatus
          isInWatchlists={isPlayerWatched(suggestion.id)}
          className={isAlreadySelected ? styles.alreadySelected : undefined}
        />
      )
    },

    [onSelectPlayer, isPlayerWatched, selectedPlayersIdsMap]
  )

  const handleEnterKey = useCallback(
    suggestion => {
      if (!selectedPlayersIdsMap[suggestion.id]) onSelectPlayer(suggestion)
    },
    [onSelectPlayer, selectedPlayersIdsMap]
  )

  const handleSeeAllPlayers = useCallback(
    search => {
      dispatch(setSearchQuery(search || '', PostScoreModalId.ADD_PLAYER))
    },
    [dispatch]
  )
  const handleCopyToClipboard = useCallback(() => copyToClipboard(WALK_UP_REG_FULL_URL), [])
  const windowWidth = useWindowSize().width

  return (
    <ModalFullScreenWTN
      modalId={PostScoreModalId.ADD_PLAYER}
      widgetElId='player-profile'
      textHeader={tSocialMatches('modals.addPlayer.header')}
    >
      <section className={styles.container}>
        <label htmlFor='player-search-bar' className={styles.searchLabel}>
          {tSocialMatches('modals.addPlayer.searchBarLabel')}
        </label>
        <SearchBar
          inputId='player-search-bar'
          minCharsToGetSuggestions={MIN_SEARCH_CHARS}
          redirectOnViewAll={false}
          viewAllEnabled={true}
          onFinishedTyping={getSuggestions}
          loading={isLoadingSuggestions}
          onSearch={handleSeeAllPlayers}
          onEnterKey={handleEnterKey}
          suggestions={suggestions}
          suggestionType={tSearchBar('player')}
          suggestionTypes={tSearchBar('players')}
          renderSuggestion={handleRenderSuggestion}
          classes={{ suggestionsWrapper: styles.searchWrapper }}
        />
        <p className={styles.shareLinkText}>
          <Trans ns='strSocialMatches' i18nKey={'modals.addPlayer.shareLinkText'}>
            Can't find a player? They may not be registered or ineligible for an account. Please
            read our{' '}
            <a href={FAQS_MY_MATCHES_HREF} rel='noopener noreferrer' target='_blank'>
              FAQs
            </a>{' '}
            before inviting players.
          </Trans>{' '}
          <BtnPrimary
            size='small'
            border={false}
            onClick={handleCopyToClipboard}
            className={styles.shareLinkBtn}
          >
            {tSocialMatches('modals.addPlayer.shareLinkBtn')}
          </BtnPrimary>
        </p>
        {searchQuery && (
          <>
            <p className={styles.infoText}>{tSocialMatches('modals.addPlayer.selectPlayer')}</p>
            <PlayerFilterBar
              searchId={PostScoreModalId.ADD_PLAYER}
              classNames={filtersStyles.current}
              stickyBehaviour={
                windowWidth < TABLET_BREAKPOINT && {
                  pinToNavbar: false,
                  shouldPortal: true,
                  zIndex: 99999,
                  offsetTop: 58,
                }
              }
            />
            <AddPlayerSearchResults
              selectedPlayersIdsMap={selectedPlayersIdsMap}
              onAddPlayer={onSelectPlayer}
              showMobileLayout={windowWidth <= DESKTOP_LAYOUT_BREAKPOINT}
            />
          </>
        )}
      </section>
    </ModalFullScreenWTN>
  )
})

export default AddPlayerModal
